import React, { useContext } from 'react'
import { Product } from '@/src/types/api-types'
import FONTS from '@/src/fonts'
import ProfileContext from '@/components/Context/ProfileContext'
import SHIPPING_MANAGER from '@/lib/basket/SHIPPING_MANAGER'
import { dataShippingFranco } from '@/components/Basket/BasketDetailCompany'
import FrancoProgress, { getAmountMaxFranco } from '@/components/Common/Header/FrancoProgress'
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material'
import COLORS from '@/src/colors'
import Card from '../Common/Card'

type Props = {
  product: Product
}

const findProductGroupToBasket = (product, basket) => {
  const group = basket?.product_groups.find(
    group => Number(group.company_id) === Number(product.company_id)
  )

  const shippingGroups = SHIPPING_MANAGER.list()
  if (!group) return { totalPrice: 0, amountFranco: 0, shippingSelected: null, group }

  const { totalPrice, amountFranco, shippingSelected } = dataShippingFranco(group, shippingGroups)
  return { totalPrice, amountFranco, shippingSelected, group }
}

const ProductShippingInfosResume: React.FC<Props> = ({ product }) => {
  const { basket, profile } = useContext(ProfileContext)
  const theme = useTheme()

  const { totalPrice, amountFranco, shippingSelected, group } = findProductGroupToBasket(
    product,
    basket
  )
  const amountMaxFranco = getAmountMaxFranco(product.shipping_extra.min_franco, profile)

  const message = () => {
    if (product.hasOwnProperty('shipping_extra')) {
      if (product.shipping_extra.free_shipping === true || amountMaxFranco == 0) {
        return <>Livraison gratuite</>
      } else if (product.shipping_extra.fixed_shipping === true) {
        return (
          <>
            Frais de livraison à partir de
            {product.shipping_extra.shippings[0].rate_info.rate_values[0].value} €
          </>
        )
      } else {
        return (
          <>
            {t({ id: 'web_franco_limit' })}
            {amountMaxFranco} €
          </>
        )
      }
    }
  }
  return (
    <>
      <Card elevate smallRadius>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          divider={
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', md: 'block' } }}
            />
          }
          spacing={{ xs: 2, md: 0 }}
        >
          {product.company_minimum_order && product.company_minimum_order > 0 && (
            <Box
              sx={{
                flex: 1,
                textAlign: 'center',
                px: { md: 2 },
                '&:not(:last-of-type)': {
                  borderBottom: { xs: 1, md: 0 },
                  borderColor: 'divider',
                  pb: { xs: 2, md: 0 },
                  mb: { xs: 2, md: 0 },
                },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                <span className="title_grey">Minimum de commande vendeur</span>
                <div
                  className="normal_text mt-4"
                  style={{ color: COLORS.storeDefault.secondary.main }}
                >
                  {product.company_minimum_order} €
                </div>
              </Typography>
            </Box>
          )}
          {product.shipping_extra.shipping_amount_fixed === false && (
            <Box
              sx={{
                flex: 1,
                textAlign: 'center',
                px: { md: 2 },
                '&:not(:last-of-type)': {
                  borderBottom: { xs: 1, md: 0 },
                  borderColor: 'divider',
                  pb: { xs: 2, md: 0 },
                  mb: { xs: 2, md: 0 },
                  color: theme.palette.primary.light,
                  fontWeight: FONTS.WEIGHT.BOLD,
                  paddingRight: 8,
                },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                <span className="title_grey">Franco</span>
                <div
                  className="normal_text mt-4"
                  style={{ color: COLORS.storeDefault.secondary.main }}
                >
                  {message()}
                </div>
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              flex: 1,
              textAlign: 'center',
              px: { md: 2 },
              // séparateurs horizontaux en mobile
              '&:not(:last-of-type)': {
                borderBottom: { xs: 1, md: 0 },
                borderColor: 'divider',
                pb: { xs: 2, md: 0 },
                mb: { xs: 2, md: 0 },
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              <span className="title_grey">Dans mon panier</span>
              <div
                style={{ color: COLORS.storeDefault.secondary.main, fontWeight: FONTS.WEIGHT.BOLD }}
              >
                {group ? (
                  product.shipping_extra.min_franco ? (
                    <FrancoProgress
                      amountFranco={amountFranco}
                      totalPrice={totalPrice}
                      group={group}
                      shippingSelected={shippingSelected}
                    />
                  ) : (
                    <div>
                      {group.products.length} article{group.products.length > 1 ? 's' : ''} de{' '}
                      {product.company_name}{' '}
                    </div>
                  )
                ) : (
                  <div
                    className="normal_text mt-4"
                    style={{ color: COLORS.storeDefault.secondary.main }}
                  >
                    Aucun article de {product.company_name}
                  </div>
                )}
              </div>
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              textAlign: 'center',
              px: { md: 2 },
              '&:not(:last-of-type)': {
                borderBottom: { xs: 1, md: 0 },
                borderColor: 'divider',
                pb: { xs: 2, md: 0 },
                mb: { xs: 2, md: 0 },
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              <span className="title_grey">Mode(s) de Livraison(s)</span>
              <div
                className="normal_text mt-4"
                style={{ color: COLORS.storeDefault.secondary.main }}
              >
                {product.shipping_extra.shippings
                  .map(shipping => shipping.shipping_name)
                  .join(', ')}
              </div>
            </Typography>
          </Box>
        </Stack>
      </Card>
    </>
  )
}

export default ProductShippingInfosResume
